// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the PURE game core. You fly a mote whose HEIGHT is your input (voice pitch,
// or a finger, or a key — the game never knows which). A luminous RIBBON weaves across
// the course; keep the mote on the ribbon to score, and a clean streak raises a combo
// multiplier. The three modes differ only in the RULE SET (see modes.ts):
//   · Glide  — a smooth wandering ribbon, forgiving, no death.
//   · Steps  — discrete held notes with rests; settle on each and hold it.
//   · Thread — a narrow ribbon strung through walls; miss a gap and the run ENDS.
//
// The core is deterministic and clock-free: it advances only via step(height, dt), which
// is what lets the sim (tests/balance) and the mechanism audit (tests/mechanism) drive
// the EXACT shipped logic without a browser or a microphone. Every course is a pure
// function of (mode, seed); a live race needs nothing on the wire.

import { makeRng, randFloat, randInt, type Rng } from '@ben-gy/game-engine/rng';
import {
  COMBO_MAX,
  COMBO_STEP_MS,
  GATE_BONUS,
  NOTE_HIT_FRAC,
  courseDurMs,
  type Mode,
} from './modes';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const smoothstep = (u: number): number => {
  const t = clamp(u, 0, 1);
  return t * t * (3 - 2 * t);
};

export interface Node {
  t: number;
  h: number;
}
export interface Platform {
  t0: number;
  t1: number;
  h: number;
}
export interface Gate {
  t: number;
  h: number;
  gap: number;
}

export interface Course {
  readonly mode: Mode;
  readonly seed: number;
  readonly durMs: number;
  readonly nodes: Node[]; // Glide + Thread: the ribbon centreline
  readonly platforms: Platform[]; // Steps: the held notes (active windows)
  readonly gates: Gate[]; // Thread: the walls to thread
}

export interface NoteResult {
  index: number;
  quality: number; // 0..1 — fraction of the platform spent on the note
  hit: boolean;
}

export interface Summary {
  score: number;
  accuracy: number; // on-ribbon fraction of the active time (0..1)
  bestCombo: number;
  died: boolean;
  reachedMs: number; // how far the run got (Thread death ends it early)
  durMs: number;
  gatesPassed: number;
  gatesTotal: number;
  notesHit: number;
  notesTotal: number;
}

export type GameEvent =
  | { t: 'enter' } // the mote landed on the ribbon (combo can now build)
  | { t: 'leave' } // the mote drifted off (combo broke)
  | { t: 'combo'; mult: number }
  | { t: 'gate'; passed: boolean }
  | { t: 'note'; index: number; quality: number; hit: boolean }
  | { t: 'die' }
  | { t: 'over' };

export interface GameState {
  readonly mode: Mode;
  readonly seed: number;
  readonly course: Course;
  clock: number;
  over: boolean;
  died: boolean;
  height: number; // last input height (0..1)
  target: number; // current ribbon height
  active: boolean; // is scoring live right now (Steps rests are inactive)
  onLine: boolean; // active && within tol of the ribbon
  err: number; // |height - target| while active, else 1
  score: number;
  combo: number; // 1..COMBO_MAX
  comboMs: number; // continuous on-ribbon time toward the next combo step
  bestCombo: number;
  onLineMs: number;
  activeMs: number;
  gateIndex: number; // next Thread gate to evaluate
  gatesPassed: number;
  platIndex: number; // Steps: current platform index, or -1 in a rest
  platOnLineMs: number;
  notes: NoteResult[];
  prevOnLine: boolean;
  summary: Summary | null;
  events: GameEvent[];
}

// ── course geometry (pure functions of a Course) ─────────────────────────────────

/** The ribbon centre height at time t (0..1). */
export function ribbonAt(course: Course, t: number): number {
  if (course.mode.kind === 'steps') return stepsRibbonAt(course, t);
  const nodes = course.nodes;
  if (nodes.length === 0) return 0.5;
  if (t <= nodes[0].t) return nodes[0].h;
  const last = nodes[nodes.length - 1];
  if (t >= last.t) return last.h;
  let i = 0;
  while (i < nodes.length - 1 && nodes[i + 1].t <= t) i++;
  const a = nodes[i];
  const b = nodes[i + 1];
  const u = (t - a.t) / (b.t - a.t);
  return a.h + (b.h - a.h) * smoothstep(u);
}

function stepsRibbonAt(course: Course, t: number): number {
  const p = course.platforms;
  if (p.length === 0) return 0.5;
  if (t <= p[0].t1) return p[0].h;
  for (let i = 0; i < p.length; i++) {
    if (t >= p[i].t0 && t <= p[i].t1) return p[i].h;
    if (i < p.length - 1 && t > p[i].t1 && t < p[i + 1].t0) {
      const u = (t - p[i].t1) / (p[i + 1].t0 - p[i].t1);
      return p[i].h + (p[i + 1].h - p[i].h) * u; // a visible connecting ramp (inactive)
    }
  }
  return p[p.length - 1].h;
}

/** Whether scoring is live at time t (Steps rests between platforms are not). */
export function activeAt(course: Course, t: number): boolean {
  if (course.mode.kind !== 'steps') return true;
  return platformIndexAt(course, t) >= 0;
}

/** Steps: which platform index t falls inside (its active window), or -1 in a rest. */
export function platformIndexAt(course: Course, t: number): number {
  const p = course.platforms;
  for (let i = 0; i < p.length; i++) {
    if (t >= p[i].t0 && t <= p[i].t1) return i;
  }
  return -1;
}

// ── course generation (pure function of mode + seed) ─────────────────────────────

function buildWander(mode: Mode, rng: Rng, durMs: number): Node[] {
  const nodes: Node[] = [{ t: 0, h: 0.5 }];
  let t = 0;
  const lo = 0.5 - mode.amp;
  const hi = 0.5 + mode.amp;
  while (t < durMs) {
    t += mode.waypointMs * randFloat(rng, 0.72, 1.3);
    nodes.push({ t: Math.min(t, durMs), h: randFloat(rng, lo, hi) });
  }
  const last = nodes[nodes.length - 1];
  if (last.t < durMs) nodes.push({ t: durMs, h: last.h });
  return nodes;
}

function buildSteps(mode: Mode, rng: Rng): Platform[] {
  const platforms: Platform[] = [];
  const levelH = (k: number): number => 0.16 + (0.68 * k) / (mode.levels - 1);
  let prev = -1;
  for (let i = 0; i < mode.steps; i++) {
    let lvl = randInt(rng, 0, mode.levels - 1);
    if (lvl === prev) lvl = (lvl + 1 + randInt(rng, 0, mode.levels - 2)) % mode.levels;
    prev = lvl;
    const base = i * (mode.platformMs + mode.gapMs);
    platforms.push({ t0: base, t1: base + mode.platformMs, h: levelH(lvl) });
  }
  return platforms;
}

export function createCourse(mode: Mode, seed: number): Course {
  const rng = makeRng(seed);
  const durMs = courseDurMs(mode);
  let nodes: Node[] = [];
  let platforms: Platform[] = [];
  let gates: Gate[] = [];

  if (mode.kind === 'steps') {
    platforms = buildSteps(mode, rng);
  } else {
    nodes = buildWander(mode, rng, durMs);
  }
  const course: Course = { mode, seed: seed >>> 0, durMs, nodes, platforms, gates };

  if (mode.kind === 'thread') {
    const g: Gate[] = [];
    for (let t = mode.gateMs; t < durMs; t += mode.gateMs) {
      g.push({ t, h: ribbonAt(course, t), gap: mode.gateGap });
    }
    gates = g;
    return { ...course, gates };
  }
  return course;
}

export function createGame(mode: Mode, seed: number): GameState {
  const course = createCourse(mode, seed);
  return {
    mode,
    seed: seed >>> 0,
    course,
    clock: 0,
    over: false,
    died: false,
    height: 0.5,
    target: ribbonAt(course, 0),
    active: activeAt(course, 0),
    onLine: false,
    err: 1,
    score: 0,
    combo: 1,
    comboMs: 0,
    bestCombo: 1,
    onLineMs: 0,
    activeMs: 0,
    gateIndex: 0,
    gatesPassed: 0,
    platIndex: platformIndexAt(course, 0),
    platOnLineMs: 0,
    notes: [],
    prevOnLine: false,
    summary: null,
    events: [],
  };
}

function finalizeNote(s: GameState): void {
  if (s.platIndex < 0) return;
  const quality = clamp(s.platOnLineMs / s.mode.platformMs, 0, 1);
  const hit = quality >= NOTE_HIT_FRAC;
  s.notes.push({ index: s.platIndex, quality, hit });
  s.events.push({ t: 'note', index: s.platIndex, quality, hit });
}

function finalize(s: GameState): void {
  if (s.mode.kind === 'steps') finalizeNote(s);
  s.over = true;
  const notesHit = s.notes.filter((n) => n.hit).length;
  s.summary = {
    score: s.score,
    accuracy: s.activeMs > 0 ? s.onLineMs / s.activeMs : 0,
    bestCombo: s.bestCombo,
    died: s.died,
    reachedMs: Math.min(s.clock, s.course.durMs),
    durMs: s.course.durMs,
    gatesPassed: s.gatesPassed,
    gatesTotal: s.course.gates.length,
    notesHit,
    notesTotal: s.notes.length,
  };
  s.events.push({ t: 'over' });
}

/**
 * Advance the run by `dtMs` under the latest input `height` (0..1). Read state and
 * drainEvents() after. Safe to call after `over` (no-op). No clock but its own.
 */
export function step(s: GameState, height: number, dtMs: number): void {
  if (s.over || dtMs <= 0) return;
  const mode = s.mode;
  s.clock += dtMs;
  s.height = clamp(height, 0, 1);

  if (s.clock >= s.course.durMs) {
    // score the final sliver up to the end, then end the run.
    s.clock = s.course.durMs;
  }

  s.target = ribbonAt(s.course, s.clock);
  s.active = activeAt(s.course, s.clock);
  const tol = mode.tol;

  if (s.active) {
    s.err = Math.abs(s.height - s.target);
    s.onLine = s.err <= tol;
    s.activeMs += dtMs;
    if (s.onLine) {
      if (!s.prevOnLine) s.events.push({ t: 'enter' });
      const a = clamp(1 - s.err / tol, 0, 1); // 1 dead-on, 0 at the edge of the band
      s.score += dtMs * mode.rate * a * s.combo;
      s.onLineMs += dtMs;
      s.comboMs += dtMs;
      if (s.comboMs >= COMBO_STEP_MS && s.combo < COMBO_MAX) {
        s.combo++;
        s.comboMs = 0;
        s.bestCombo = Math.max(s.bestCombo, s.combo);
        s.events.push({ t: 'combo', mult: s.combo });
      }
    } else {
      if (s.prevOnLine) s.events.push({ t: 'leave' });
      if (s.combo > 1 || s.comboMs > 0) {
        s.combo = 1;
        s.comboMs = 0;
      }
    }
    s.prevOnLine = s.onLine;
  } else {
    // A Steps rest: no scoring, and the combo is held (you are travelling to the
    // next note, not failing). prevOnLine clears so re-entry re-emits 'enter'.
    s.onLine = false;
    s.err = 1;
    s.prevOnLine = false;
  }

  // Steps: finalise a note whenever we cross a platform boundary.
  if (mode.kind === 'steps') {
    const cur = platformIndexAt(s.course, s.clock);
    if (cur !== s.platIndex) {
      finalizeNote(s);
      s.platIndex = cur;
      s.platOnLineMs = 0;
    }
    if (cur >= 0 && s.onLine) s.platOnLineMs += dtMs;
  }

  // Thread: evaluate any walls we have reached. A missed gap ends the run.
  if (mode.oneLife && s.course.gates.length) {
    while (s.gateIndex < s.course.gates.length && s.clock >= s.course.gates[s.gateIndex].t) {
      const g = s.course.gates[s.gateIndex++];
      const threaded = Math.abs(s.height - g.h) <= g.gap;
      s.events.push({ t: 'gate', passed: threaded });
      if (threaded) {
        s.gatesPassed++;
        s.score += GATE_BONUS * s.combo;
      } else {
        s.died = true;
        s.events.push({ t: 'die' });
        finalize(s);
        return;
      }
    }
  }

  if (s.clock >= s.course.durMs) finalize(s);
}

/** Take and clear the queued juice/audit events. */
export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  return s.events.splice(0);
}
